import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { TranslateModule } from '@ngx-translate/core';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Store } from '@ngxs/store';
import { Observable, of, takeUntil } from 'rxjs';

import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import { AngularMaterialModule } from '@shared/modules/angular-material.module';
import { SearchByItem, GetDefaultSearchByItem } from '@shared/classes/general';
import { DatepickerNormalFormat } from '@shared/constants';
import { smartSearchType } from './smart-search-form.constant';
import { CommonSearchCriteria, GenerateDefaultCommonSearchCriteria } from '@app/shared/models/common-data.model';
import { BasedComponent } from '@shared/components/based.component';

@Component({
  selector: 'app-smart-search-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerNormalFormat },
  ],
  imports: [SpinnerButtonComponent, CommonModule, TranslateModule, AngularMaterialModule, NgxsFormPluginModule, ReactiveFormsModule],
  templateUrl: './smart-search-form.component.html',
  styleUrl: './smart-search-form.component.css'
})
export class SmartSearchFormComponent extends BasedComponent {
  store = inject(Store);
  dateAdapter = inject(DateAdapter);
  formBuilder = inject(FormBuilder);
  datePipe = inject(DatePipe);

  @Input() formPath: string;
  @Input() searchByList: SearchByItem[] = [];
  @Input() onSearch: () => void;
  @Input() searching$: Observable<boolean>;
  @Input() defaultSearchCriteria: CommonSearchCriteria[] = [];

  @Input() defaultSearchCriteria$: Observable<CommonSearchCriteria[]> = of([]);
  @Input() formClearOnDestroy: boolean = false;
  @Input() noMoreFilter: boolean = false;

  smartSearchType = smartSearchType;
  smartSearchForm: FormGroup;
  currentSearchByItemList: SearchByItem[] = [];

  constructor() {
    super();
    this.initForm();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initSearchCriteria();
  }

  initSearchCriteria = () => {
    this.defaultSearchCriteria$.pipe(takeUntil(this.destroy$)).subscribe(
      defaultSearchCriteria => {
        this.searchCriteria.clear();
        this.currentSearchByItemList = [];
        if (defaultSearchCriteria && defaultSearchCriteria.length > 0) {
          defaultSearchCriteria.forEach(item => this.onMoreFilter(item));
        }
        else {
          this.onMoreFilter();
        }
      }
    )
  }

  initForm = () => {
    this.smartSearchForm = this.formBuilder.group({
      searchCriteria: this.formBuilder.array([])
    });
  }

  get searchCriteria() {
    return this.smartSearchForm.controls["searchCriteria"] as FormArray;
  }

  onMoreFilter = (addedSearchCriteria: CommonSearchCriteria = GenerateDefaultCommonSearchCriteria()) => {
    const searchForm = this.formBuilder.group({
      searchBy: [addedSearchCriteria.searchBy],
      searchValue: [addedSearchCriteria.searchValue],
      searchValueStart: [addedSearchCriteria.searchValueStart],
      searchValueEnd: [addedSearchCriteria.searchValueEnd],
      isSearchByDisabled: [addedSearchCriteria.isSearchByDisabled]
    });

    if (addedSearchCriteria.isSearchByDisabled) {
      searchForm.get('searchBy').disable();
    }

    this.searchCriteria.push(searchForm);
    this.currentSearchByItemList.push(this.getSearchByItem(addedSearchCriteria.searchBy));
  }

  onDeleteFilter = (index: number) => {
    this.searchCriteria.removeAt(index);
    this.currentSearchByItemList.splice(index, 1);
  }

  searchByChanged = (event: any, needResetValue: boolean, index: number) => {
    const selectedItem = this.getSearchByItem(event.value);
    this.currentSearchByItemList[index] = selectedItem;

    if (needResetValue)
      this.resetSearchValue(index);
  }

  resetSearchValue = (index: number) => {
    this.store.dispatch(new UpdateFormValue({
      value: { searchValue: '', searchValueStart: '', searchValueEnd: '' },
      path: this.formPath,
      propertyPath: `searchCriteria.${index}`
    }));
  }

  getSearchByItem = (searchBy: string) => {
    if (!this.searchByList) {
      return GetDefaultSearchByItem();
    }

    return this.searchByList.find(item => item.searchBy === searchBy) ?? GetDefaultSearchByItem();
  }
}
